import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { getToken } from '../../../selectors/base.selectors';
import { BaseActionTypes } from '../../../actions/base.actions';
import { EMPTY, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from '../../../index';
import { HttpService } from '../../../../services/http.service';
import { GraphQlEffectService } from '../../../../services/graph-ql-effect.service';
import { DoctorCatalogActions } from '../../../actions/mdqx/doctor/doctor-catalog.actions';
import { DoctorCatalogEntity } from '../../../../entities/mdqx/doctor/doctor-catalog.entity';
import { environment } from '../../../../../../environments/environment';
import { concatMap } from 'rxjs/internal/operators/concatMap';
import { GetDoctorsRequest } from '../../../actions/mdqx/doctor/doctor.actions';


enum ErrorCodes {
    Load = 'Facharztkatalog abrufen fehlgeschlagen',
    Change = 'Facharztkatalog bearbeiten fehlgeschlagen',
    Delete = 'Facharztkatalog löschen fehlgeschlagen',
}

@Injectable()
export class DoctorCatalogEffects {
    errors$ = createEffect(() => this.actions$.pipe(
        ofType(
            DoctorCatalogActions.loadFailure,
            DoctorCatalogActions.changeFailure,
            DoctorCatalogActions.createFailure,
            DoctorCatalogActions.deleteFailure,
        ),
        map((action) => BaseActionTypes.ErrorAction({
            Payload: {
                ToasterMessage: action.ToasterMessage,
                Err: action.Errors,
            }
        }))
    ))

    Get$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DoctorCatalogActions.loadRequest),
                withLatestFrom(this.store$.select(getToken)),
                switchMap(([{ Payload }, token]) => {
                    const queryString = `query {
                    doctorcatalog{
                        ${DoctorCatalogEntity.GqlFields.join(',')}
                    }
                }`;
                    return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: true }, '', environment.mdqxGatewayApiUrl).pipe(
                        tap((res) => {
                            this.store$.dispatch(
                                res && res.doctorcatalog
                                    ?
                                    DoctorCatalogActions.loadSuccess({
                                        Payload: res.doctorcatalog.map((val) => DoctorCatalogEntity.fromBackend(val)),
                                    })
                                    : DoctorCatalogActions.loadFailure(
                                        { ToasterMessage: ErrorCodes.Load,
                                            Errors: 'wrong response doctorcatalog'},
                                    ),
                            );
                        }),
                        catchError((err, caught) => {
                            this.store$.dispatch(
                                DoctorCatalogActions.loadFailure({
                                    ToasterMessage: ErrorCodes.Load,
                                    Errors: err,
                                }),
                            );
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    Put$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DoctorCatalogActions.changeRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  changeDoctorcatalog(id: ${Payload.id} name: "${Payload.name}"){
                  ${DoctorCatalogEntity.GqlFields.join(',')}
                  }
                }
                `
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    map((res) => {
                        if (res?.changeDoctorcatalog) {
                            return DoctorCatalogActions.changeSuccess({Payload: DoctorCatalogEntity.fromBackend(res.changeDoctorcatalog)});
                        } else {
                            return DoctorCatalogActions.changeFailure({
                                ToasterMessage: ErrorCodes.Change,
                                Errors: 'wrong response changeDoctorcatalog',
                            });
                        }
                    }),
                    catchError((err, caught) => {
                        return of(DoctorCatalogActions.changeFailure({
                            ToasterMessage: ErrorCodes.Change,
                            Errors: err,
                        }));
                    }),
                );
            }),
        ),
    );
    Delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DoctorCatalogActions.deleteRequest),
            withLatestFrom(this.store$.select(getToken)),
            concatMap(([{ Payload }, token]) => {
                const queryString = `
                mutation{
                  deleteDoctorcatalog(id: ${Payload.id})
                }
                `
                return this.gatewayHttpService.graphQl({ query: queryString }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                    concatMap((res) => {
                        if (res?.deleteDoctorcatalog) {
                            return [DoctorCatalogActions.deleteSuccess({Payload: Payload.id}), new GetDoctorsRequest()];
                        } else {
                            return [DoctorCatalogActions.deleteFailure({
                                ToasterMessage: ErrorCodes.Delete,
                                Errors: 'wrong response deleteDoctorcatalog',
                            })];
                        }
                    }),
                    catchError((err, caught) => {
                        return of(DoctorCatalogActions.deleteFailure({
                            ToasterMessage: ErrorCodes.Delete,
                            Errors: err,
                        }));
                    }),
                );
            }),
        ),
    );
    constructor(private actions$: Actions, private store$: Store<State>, private gatewayHttpService: HttpService, private graphQlEffectService: GraphQlEffectService) {}
}
